import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  FormHelperText,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  Switch,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
  History as HistoryIcon,
  Pause as PauseIcon,
  Schedule as ScheduleIcon,
  MoreVert as MoreVertIcon,
  CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

const DAYS_OF_MONTH = Array.from({ length: 28 }, (_, i) => i + 1);

const ScheduledReports = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [locations, setLocations] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuSchedule, setMenuSchedule] = useState(null);
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [runSchedule, setRunSchedule] = useState(null);
  const [runStartDate, setRunStartDate] = useState('');
  const [runEndDate, setRunEndDate] = useState('');

  const { apiCall, getLocations } = useApi();
  const { user, userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'admin';

  // Form state
  const [formData, setFormData] = useState({
    reportName: '',
    reportType: 'settlement',
    frequency: 'daily',
    dayOfWeek: 1,
    dayOfMonth: 1,
    executionTime: '06:00',
    deliveryEmail: '',
    ccEmails: '',
    locations: [],
    isActive: true
  });

  useEffect(() => {
    loadSchedules();
    loadLocations();
  }, []);

  const loadSchedules = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiCall('/api/v1/scheduled-reports', 'GET');
      setSchedules(data.schedules || []);
    } catch (err) {
      console.error('Error loading schedules:', err);
      setError('Failed to load scheduled reports');
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    try {
      // Get available locations using same method as Settlement Reports
      const locationsResponse = await getLocations();
      // Extract unique location names (same pattern as Settlement Reports)
      const uniqueLocations = [...new Set((locationsResponse || []).map(loc => loc.LocationName))].sort();
      setLocations(uniqueLocations);
    } catch (err) {
      console.error('Error loading locations:', err);
    }
  };

  const handleOpenDialog = (schedule = null) => {
    if (schedule) {
      setEditingSchedule(schedule);
      setFormData({
        reportName: schedule.reportName || '',
        reportType: schedule.reportType || 'settlement',
        frequency: schedule.frequency || 'daily',
        dayOfWeek: schedule.dayOfWeek !== null ? schedule.dayOfWeek : 1,
        dayOfMonth: schedule.dayOfMonth || 1,
        executionTime: schedule.executionTime?.substring(0, 5) || '06:00',
        deliveryEmail: schedule.deliveryEmail || '',
        ccEmails: (schedule.ccEmails || []).join(', '),
        locations: schedule.locations || [],
        isActive: schedule.isActive !== false
      });
    } else {
      setEditingSchedule(null);
      setFormData({
        reportName: '',
        reportType: 'settlement',
        frequency: 'daily',
        dayOfWeek: 1,
        dayOfMonth: 1,
        executionTime: '06:00',
        deliveryEmail: user?.email || '',
        ccEmails: '',
        locations: [],
        isActive: true
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSchedule(null);
    setError(null);
  };

  const handleSubmit = async () => {
    try {
      setError(null);

      if (!formData.reportName.trim()) {
        setError('Report name is required');
        return;
      }

      if (formData.locations.length === 0) {
        setError('At least one location is required');
        return;
      }

      const payload = {
        reportName: formData.reportName.trim(),
        reportType: formData.reportType,
        frequency: formData.frequency,
        executionTime: formData.executionTime + ':00',
        deliveryEmail: formData.deliveryEmail || user?.email,
        ccEmails: formData.ccEmails ? formData.ccEmails.split(',').map(e => e.trim()).filter(e => e) : [],
        locations: formData.locations,
        isActive: formData.isActive
      };

      if (formData.frequency === 'weekly') {
        payload.dayOfWeek = formData.dayOfWeek;
      } else if (formData.frequency === 'monthly') {
        payload.dayOfMonth = formData.dayOfMonth;
      }

      if (editingSchedule) {
        await apiCall(`/api/v1/scheduled-reports/${editingSchedule.id}`, 'PUT', payload);
      } else {
        await apiCall('/api/v1/scheduled-reports', 'POST', payload);
      }

      handleCloseDialog();
      loadSchedules();
    } catch (err) {
      console.error('Error saving schedule:', err);
      setError(err.message || 'Failed to save schedule');
    }
  };

  const handleDelete = async () => {
    if (!scheduleToDelete) return;

    try {
      await apiCall(`/api/v1/scheduled-reports/${scheduleToDelete.id}`, 'DELETE');
      setDeleteDialogOpen(false);
      setScheduleToDelete(null);
      loadSchedules();
    } catch (err) {
      console.error('Error deleting schedule:', err);
      setError('Failed to delete schedule');
    }
  };

  const handleRunNow = (schedule) => {
    // Default to yesterday
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yd = yesterday.toISOString().split('T')[0];

    setRunSchedule(schedule);
    setRunStartDate(yd);
    setRunEndDate(yd);
    setRunDialogOpen(true);
  };

  const handleExecuteRun = async () => {
    if (!runSchedule) return;
    try {
      setRunDialogOpen(false);
      setActionLoading(runSchedule.id);
      const payload = {};
      if (runStartDate) payload.startDate = runStartDate;
      if (runEndDate) payload.endDate = runEndDate;
      await apiCall(`/api/v1/scheduled-reports/${runSchedule.id}/run`, 'POST', payload);
      alert('Report execution triggered. You will receive an email shortly.');
      loadSchedules();
    } catch (err) {
      console.error('Error running schedule:', err);
      alert('Failed to trigger report execution');
    } finally {
      setActionLoading(null);
      setRunSchedule(null);
    }
  };

  const handleToggleActive = async (schedule) => {
    try {
      setActionLoading(schedule.id);
      await apiCall(`/api/v1/scheduled-reports/${schedule.id}`, 'PUT', {
        isActive: !schedule.isActive
      });
      loadSchedules();
    } catch (err) {
      console.error('Error toggling schedule:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleViewHistory = async (schedule) => {
    try {
      setSelectedSchedule(schedule);
      setHistoryLoading(true);
      setHistoryDialogOpen(true);

      const data = await apiCall(`/api/v1/scheduled-reports/${schedule.id}/history`, 'GET');
      setHistoryData(data.history || []);
    } catch (err) {
      console.error('Error loading history:', err);
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };

  const getFrequencyLabel = (schedule) => {
    if (schedule.frequency === 'daily') return 'Daily';
    if (schedule.frequency === 'weekly') {
      const day = DAYS_OF_WEEK.find(d => d.value === schedule.dayOfWeek);
      return `Weekly (${day?.label || 'Unknown'})`;
    }
    if (schedule.frequency === 'monthly') {
      return `Monthly (Day ${schedule.dayOfMonth})`;
    }
    return schedule.frequency;
  };

  const getStatusChip = (schedule) => {
    if (!schedule.isActive) {
      return <Chip label="Paused" size="small" color="default" />;
    }
    if (schedule.consecutiveFailures > 0) {
      return <Chip label="Failed" size="small" color="error" />;
    }
    return <Chip label="Active" size="small" color="success" />;
  };

  if (loading && schedules.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <ScheduleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Scheduled Reports
        </Typography>
        <Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadSchedules}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create Schedule
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          {schedules.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary" gutterBottom>
                No scheduled reports yet
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Create Your First Schedule
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Frequency</strong></TableCell>
                    <TableCell><strong>Locations</strong></TableCell>
                    <TableCell><strong>Next Run</strong></TableCell>
                    <TableCell><strong>Last Run</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schedules.map((schedule) => (
                    <TableRow key={schedule.id} hover>
                      <TableCell>{schedule.reportName}</TableCell>
                      <TableCell>
                        <Chip
                          label={schedule.reportType}
                          size="small"
                          color={schedule.reportType === 'settlement' ? 'primary' : 'secondary'}
                        />
                      </TableCell>
                      <TableCell>{getFrequencyLabel(schedule)}</TableCell>
                      <TableCell>
                        {schedule.locations?.length > 2 ? (
                          <Tooltip title={schedule.locations.join(', ')}>
                            <span>{schedule.locations.length} locations</span>
                          </Tooltip>
                        ) : (
                          schedule.locations?.join(', ') || '-'
                        )}
                      </TableCell>
                      <TableCell>{formatDateTime(schedule.nextExecutionAt)}</TableCell>
                      <TableCell>{formatDateTime(schedule.lastRun)}</TableCell>
                      <TableCell>{getStatusChip(schedule)}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            setMenuAnchorEl(e.currentTarget);
                            setMenuSchedule(schedule);
                          }}
                          disabled={actionLoading === schedule.id}
                        >
                          {actionLoading === schedule.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <MoreVertIcon />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => { setMenuAnchorEl(null); setMenuSchedule(null); }}
      >
        <MenuItem onClick={() => {
          handleRunNow(menuSchedule);
          setMenuAnchorEl(null);
          setMenuSchedule(null);
        }}>
          <ListItemIcon><PlayIcon color="primary" fontSize="small" /></ListItemIcon>
          <ListItemText>Run Now</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          handleToggleActive(menuSchedule);
          setMenuAnchorEl(null);
          setMenuSchedule(null);
        }}>
          <ListItemIcon>
            {menuSchedule?.isActive ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{menuSchedule?.isActive ? 'Pause' : 'Resume'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          handleViewHistory(menuSchedule);
          setMenuAnchorEl(null);
          setMenuSchedule(null);
        }}>
          <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
          <ListItemText>History</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          handleOpenDialog(menuSchedule);
          setMenuAnchorEl(null);
          setMenuSchedule(null);
        }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
          setScheduleToDelete(menuSchedule);
          setDeleteDialogOpen(true);
          setMenuAnchorEl(null);
          setMenuSchedule(null);
        }}>
          <ListItemIcon><DeleteIcon color="error" fontSize="small" /></ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSchedule ? 'Edit Schedule' : 'Create Schedule'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              label="Report Name"
              fullWidth
              value={formData.reportName}
              onChange={(e) => setFormData({ ...formData, reportName: e.target.value })}
              sx={{ mb: 2 }}
              placeholder="e.g., Weekly Settlement Report"
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Report Type</Typography>
              <ToggleButtonGroup
                value={formData.reportType}
                exclusive
                onChange={(e, value) => value && setFormData({ ...formData, reportType: value })}
                fullWidth
                size="small"
              >
                <ToggleButton value="settlement">Settlement</ToggleButton>
                <ToggleButton value="transactions">Transactions</ToggleButton>
                {isAdmin && <ToggleButton value="surcharge">Surcharge</ToggleButton>}
              </ToggleButtonGroup>
            </FormControl>

            <Autocomplete
              multiple
              options={locations}
              value={formData.locations}
              onChange={(e, value) => setFormData({ ...formData, locations: value })}
              renderInput={(params) => (
                <TextField {...params} label="Locations" placeholder="Select locations" />
              )}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Frequency</Typography>
              <ToggleButtonGroup
                value={formData.frequency}
                exclusive
                onChange={(e, value) => value && setFormData({ ...formData, frequency: value })}
                fullWidth
                size="small"
              >
                <ToggleButton value="daily">Daily</ToggleButton>
                <ToggleButton value="weekly">Weekly</ToggleButton>
                <ToggleButton value="monthly">Monthly</ToggleButton>
              </ToggleButtonGroup>
            </FormControl>

            {formData.frequency === 'weekly' && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Day of Week</InputLabel>
                <Select
                  value={formData.dayOfWeek}
                  onChange={(e) => setFormData({ ...formData, dayOfWeek: e.target.value })}
                  label="Day of Week"
                >
                  {DAYS_OF_WEEK.map((day) => (
                    <MenuItem key={day.value} value={day.value}>{day.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {formData.frequency === 'monthly' && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Day of Month</InputLabel>
                <Select
                  value={formData.dayOfMonth}
                  onChange={(e) => setFormData({ ...formData, dayOfMonth: e.target.value })}
                  label="Day of Month"
                >
                  {DAYS_OF_MONTH.map((day) => (
                    <MenuItem key={day} value={day}>{day}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>Days 1-28 only to avoid month-end issues</FormHelperText>
              </FormControl>
            )}

            <TextField
              label="Execution Time (EST)"
              type="time"
              fullWidth
              value={formData.executionTime}
              onChange={(e) => setFormData({ ...formData, executionTime: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Delivery Email"
              fullWidth
              value={formData.deliveryEmail}
              onChange={(e) => setFormData({ ...formData, deliveryEmail: e.target.value })}
              sx={{ mb: 2 }}
              placeholder={user?.email}
            />

            <TextField
              label="CC Emails (comma-separated)"
              fullWidth
              value={formData.ccEmails}
              onChange={(e) => setFormData({ ...formData, ccEmails: e.target.value })}
              sx={{ mb: 2 }}
              placeholder="email1@example.com, email2@example.com"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editingSchedule ? 'Save Changes' : 'Create Schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Schedule</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{scheduleToDelete?.reportName}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Run Now Dialog - Date Selector */}
      <Dialog open={runDialogOpen} onClose={() => setRunDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Run Report: {runSchedule?.reportName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a date range for the report. Defaults to yesterday.
            </Typography>
            <TextField
              label="Start Date"
              type="date"
              fullWidth
              value={runStartDate}
              onChange={(e) => setRunStartDate(e.target.value)}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              fullWidth
              value={runEndDate}
              onChange={(e) => setRunEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRunDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<PlayIcon />}
            onClick={handleExecuteRun}
            disabled={!runStartDate || !runEndDate}
          >
            Run Report
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <Dialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Execution History: {selectedSchedule?.reportName}
        </DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : historyData.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={3}>
              No execution history yet
            </Typography>
          ) : (
            <TableContainer component={Paper} sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Started</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date Range</TableCell>
                    <TableCell>Records</TableCell>
                    <TableCell>Email Sent</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyData.map((h) => (
                    <TableRow key={h.id}>
                      <TableCell>{formatDateTime(h.executionStartedAt)}</TableCell>
                      <TableCell>
                        <Chip
                          label={h.executionStatus}
                          size="small"
                          color={
                            h.executionStatus === 'success' ? 'success' :
                            h.executionStatus === 'failed' ? 'error' :
                            h.executionStatus === 'running' ? 'info' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {h.reportDateRangeStart && h.reportDateRangeEnd
                          ? `${h.reportDateRangeStart} - ${h.reportDateRangeEnd}`
                          : '-'}
                      </TableCell>
                      <TableCell>{h.recordsGenerated || '-'}</TableCell>
                      <TableCell>
                        {h.emailSent ? (
                          <Chip label="Sent" size="small" color="success" />
                        ) : (
                          <Chip label="No" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        {h.errorMessage ? (
                          <Tooltip title={h.errorMessage}>
                            <Chip label="Error" size="small" color="error" />
                          </Tooltip>
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ScheduledReports;
